import React, { useState } from 'react';
import { useApi } from '../hooks/useApi';
import { useForm } from 'react-hook-form';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LineChart, Line } from 'recharts';
import { formatCurrency } from '../utils/constants';
import { Wallet, Calculator, Shield, AlertTriangle, PieChart as PieIcon } from 'lucide-react';

const SIPPlanner = () => {
  const { post, loading, error } = useApi();
  const [result, setResult] = useState(null);
  const [incomeResult, setIncomeResult] = useState(null);
  const [activeTab, setActiveTab] = useState('calculator');

  // SIP Calculator form
  const { register: regCalc, handleSubmit: handleCalc } = useForm({
    defaultValues: { monthlyAmount: 5000, durationYears: 10, expectedReturnRate: 12 },
  });

  // Income-aware form
  const { register: regIncome, handleSubmit: handleIncome } = useForm({
    defaultValues: { monthlyIncome: 50000, essentialExpenses: 25000, emergencyBufferPct: 10, durationYears: 15 },
  });

  const onCalculate = async (data) => {
    try {
      const response = await post('/sip/calculate', {
        monthlyAmount: Number(data.monthlyAmount),
        durationYears: Number(data.durationYears),
        expectedReturnRate: Number(data.expectedReturnRate),
      });
      setResult(response);
    } catch { /* handled */ }
  };

  const onIncomeAware = async (data) => {
    try {
      const response = await post('/sip/income-aware', {
        monthlyIncome: Number(data.monthlyIncome),
        essentialExpenses: Number(data.essentialExpenses),
        emergencyBufferPct: Number(data.emergencyBufferPct),
        durationYears: Number(data.durationYears),
      });
      setIncomeResult(response);
    } catch { /* handled */ }
  };

  const handleSave = async () => {
    if (!result) return;
    try {
      await post('/sip/save', {
        monthlyAmount: result.monthlyAmount,
        durationYears: result.durationYears,
        expectedReturnRate: result.expectedReturnRate,
      });
      alert('SIP plan saved!');
    } catch { /* handled */ }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload?.length) {
      return (
        <div className="bg-white p-3 border rounded-lg shadow-lg text-sm">
          <p className="font-semibold text-gray-700">Year {label}</p>
          {payload.map((entry, idx) => (
            <p key={idx} style={{ color: entry.color }}>
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-6">
        <Wallet className="h-8 w-8 text-primary-600" />
        <div>
          <h1 className="text-3xl font-bold text-gray-900">SIP Planner</h1>
          <p className="text-gray-500">Plan your investments with income-aware calculation</p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">{error}</div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {[
          { key: 'calculator', label: 'SIP Calculator', icon: Calculator },
          { key: 'income', label: 'Income-Aware SIP', icon: Shield },
        ].map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab.key ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Icon className="h-4 w-4" /> {tab.label}
            </button>
          );
        })}
      </div>

      {/* ───── Tab: SIP Calculator ───── */}
      {activeTab === 'calculator' && (
        <div className="space-y-6">
          <form onSubmit={handleCalc(onCalculate)} className="card">
            <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Calculator className="h-5 w-5 text-primary-600" /> SIP Calculator
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="label">Monthly Investment (₹)</label>
                <input type="number" className="input-field" {...regCalc('monthlyAmount', { required: true, min: 500 })} />
              </div>
              <div>
                <label className="label">Duration (Years)</label>
                <input type="number" className="input-field" {...regCalc('durationYears', { required: true, min: 1, max: 40 })} />
              </div>
              <div>
                <label className="label">Expected Return (%)</label>
                <input type="number" step="0.5" className="input-field" {...regCalc('expectedReturnRate', { required: true, min: 1, max: 30 })} />
              </div>
            </div>
            <button type="submit" className="btn-primary w-full mt-4" disabled={loading}>
              {loading ? 'Calculating...' : 'Calculate SIP'}
            </button>
          </form>

          {result && (
            <>
              {/* Result Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="card text-center">
                  <p className="text-sm text-gray-500 mb-1">Total Invested</p>
                  <p className="text-2xl font-bold text-gray-800">{formatCurrency(result.totalInvested)}</p>
                </div>
                <div className="card text-center">
                  <p className="text-sm text-gray-500 mb-1">Wealth Gained</p>
                  <p className="text-2xl font-bold text-green-600">{formatCurrency(result.wealthGained)}</p>
                </div>
                <div className="card text-center bg-primary-50 border-primary-200">
                  <p className="text-sm text-primary-600 mb-1">Projected Corpus</p>
                  <p className="text-2xl font-bold text-primary-700">{formatCurrency(result.projectedCorpus)}</p>
                </div>
              </div>

              {/* 3-Scenario Chart */}
              {result.multiScenario && (
                <div className="card">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4">3-Scenario Projection</h4>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={result.multiScenario}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="year" />
                        <YAxis tickFormatter={(v) => formatCurrency(v)} width={80} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Line type="monotone" dataKey="invested" name="Invested" stroke="#94a3b8" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="pessimistic" name="Pessimistic (8%)" stroke="#ef4444" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="realistic" name="Realistic (12%)" stroke="#3b82f6" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="optimistic" name="Optimistic (15%)" stroke="#22c55e" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Scenario Summary Cards */}
                  {result.scenarioSummary && (
                    <div className="grid grid-cols-3 gap-3 mt-4">
                      {result.scenarioSummary.map((s) => (
                        <div key={s.name} className={`text-center p-3 rounded-lg ${
                          s.name === 'Pessimistic' ? 'bg-red-50' : s.name === 'Optimistic' ? 'bg-green-50' : 'bg-blue-50'
                        }`}>
                          <p className="text-xs font-medium text-gray-600">{s.name} ({s.rate}%)</p>
                          <p className={`text-lg font-bold ${
                            s.name === 'Pessimistic' ? 'text-red-600' : s.name === 'Optimistic' ? 'text-green-600' : 'text-blue-600'
                          }`}>{formatCurrency(s.finalCorpus)}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Asset Class Breakdown */}
              {result.assetBreakdown && (
                <div className="card">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <PieIcon className="h-5 w-5 text-primary-600" /> SIP by Asset Class
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(result.assetBreakdown).filter(([, v]) => v > 0).map(([asset, amount]) => (
                      <div key={asset} className="text-center p-4 bg-gray-50 rounded-lg">
                        <p className="text-xs font-medium text-gray-500 uppercase">{asset}</p>
                        <p className="text-xl font-bold text-gray-800">{formatCurrency(amount)}</p>
                        <p className="text-xs text-gray-400">per month</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Single-scenario growth chart */}
              {result.projections && (
                <div className="card">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4">Corpus Growth</h4>
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={result.projections}>
                        <defs>
                          <linearGradient id="colorCorpus" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="colorInvested" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="year" />
                        <YAxis tickFormatter={(v) => formatCurrency(v)} width={80} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Area type="monotone" dataKey="invested" name="Invested" stroke="#22c55e" fill="url(#colorInvested)" strokeWidth={2} />
                        <Area type="monotone" dataKey="corpus" name="Corpus" stroke="#3b82f6" fill="url(#colorCorpus)" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              <button onClick={handleSave} className="btn-primary w-full">
                Save SIP Plan
              </button>
            </>
          )}
        </div>
      )}

      {/* ───── Tab: Income-Aware SIP ───── */}
      {activeTab === 'income' && (
        <div className="space-y-6">
          <form onSubmit={handleIncome(onIncomeAware)} className="card">
            <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary-600" /> Income-Aware SIP Calculator
            </h4>
            <p className="text-sm text-gray-500 mb-4">
              Auto-calculates your investible surplus after expenses & emergency buffer.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="label">Monthly Income (₹)</label>
                <input type="number" className="input-field" {...regIncome('monthlyIncome', { required: true, min: 1000 })} />
              </div>
              <div>
                <label className="label">Essential Expenses (₹)</label>
                <input type="number" className="input-field" {...regIncome('essentialExpenses', { required: true, min: 0 })} />
              </div>
              <div>
                <label className="label">Emergency Buffer (%)</label>
                <input type="number" className="input-field" {...regIncome('emergencyBufferPct', { min: 0, max: 50 })} />
              </div>
              <div>
                <label className="label">Duration (Years)</label>
                <input type="number" className="input-field" {...regIncome('durationYears', { required: true, min: 1, max: 40 })} />
              </div>
            </div>
            <button type="submit" className="btn-primary w-full mt-4" disabled={loading}>
              {loading ? 'Calculating...' : 'Calculate Smart SIP'}
            </button>
          </form>

          {incomeResult && (
            <>
              {/* Surplus Breakdown */}
              <div className="card">
                <h4 className="text-lg font-semibold text-gray-800 mb-4">Income Breakdown</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-gray-50 rounded-lg text-center">
                    <p className="text-xs text-gray-500">Monthly Income</p>
                    <p className="text-xl font-bold text-gray-800">{formatCurrency(incomeResult.surplus.monthlyIncome)}</p>
                  </div>
                  <div className="p-4 bg-red-50 rounded-lg text-center">
                    <p className="text-xs text-gray-500">Expenses</p>
                    <p className="text-xl font-bold text-red-600">{formatCurrency(incomeResult.surplus.essentialExpenses)}</p>
                  </div>
                  <div className="p-4 bg-yellow-50 rounded-lg text-center">
                    <p className="text-xs text-gray-500">Emergency Buffer</p>
                    <p className="text-xl font-bold text-yellow-600">{formatCurrency(incomeResult.surplus.emergencyBuffer)}</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg text-center">
                    <p className="text-xs text-gray-500">Investible Surplus</p>
                    <p className="text-xl font-bold text-green-600">{formatCurrency(incomeResult.surplus.investibleSurplus)}</p>
                  </div>
                  <div className="p-4 bg-primary-50 rounded-lg text-center">
                    <p className="text-xs text-primary-600">Suggested SIP</p>
                    <p className="text-xl font-bold text-primary-700">{formatCurrency(incomeResult.surplus.suggestedSIP)}</p>
                  </div>
                </div>
              </div>

              {/* Emergency Fund */}
              <div className="card flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-gray-800">Emergency Fund Required</p>
                  <p className="text-sm text-gray-600">
                    You need <span className="font-semibold">{formatCurrency(incomeResult.surplus.emergencyFundRequired)}</span>{' '}
                    (6 months of expenses) as an emergency fund before aggressive investing.
                  </p>
                </div>
              </div>

              {/* SIP Result */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="card text-center">
                  <p className="text-sm text-gray-500 mb-1">Total Invested</p>
                  <p className="text-2xl font-bold text-gray-800">{formatCurrency(incomeResult.sip.totalInvested)}</p>
                </div>
                <div className="card text-center">
                  <p className="text-sm text-gray-500 mb-1">Wealth Gained</p>
                  <p className="text-2xl font-bold text-green-600">{formatCurrency(incomeResult.sip.wealthGained)}</p>
                </div>
                <div className="card text-center bg-primary-50 border-primary-200">
                  <p className="text-sm text-primary-600 mb-1">Projected Corpus</p>
                  <p className="text-2xl font-bold text-primary-700">{formatCurrency(incomeResult.sip.projectedCorpus)}</p>
                </div>
              </div>

              {/* 3-Scenario for Income-Aware */}
              {incomeResult.multiScenario && (
                <div className="card">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4">3-Scenario Projection</h4>
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={incomeResult.multiScenario}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="year" />
                        <YAxis tickFormatter={(v) => formatCurrency(v)} width={80} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Line type="monotone" dataKey="invested" name="Invested" stroke="#94a3b8" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="pessimistic" name="Pessimistic (8%)" stroke="#ef4444" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="realistic" name="Realistic (12%)" stroke="#3b82f6" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="optimistic" name="Optimistic (15%)" stroke="#22c55e" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Asset Breakdown */}
              {incomeResult.assetBreakdown && (
                <div className="card">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <PieIcon className="h-5 w-5 text-primary-600" /> Monthly SIP by Asset Class
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(incomeResult.assetBreakdown).filter(([, v]) => v > 0).map(([asset, amount]) => (
                      <div key={asset} className="text-center p-4 bg-gray-50 rounded-lg">
                        <p className="text-xs font-medium text-gray-500 uppercase">{asset}</p>
                        <p className="text-xl font-bold text-gray-800">{formatCurrency(amount)}</p>
                        <p className="text-xs text-gray-400">per month</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SIPPlanner;
